import { Typography } from '@mui/material';
import { styled } from '@mui/system';
import { type FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useEffectOnce } from '../../../../../../../hooks/use-effect-once';
import { ConfigurationsContainer } from '../../../containers/ConfigurationsContainer';
import { SYNC_DIRECTIONS } from '../../../stripe-components/configure-stripe-integration/StripeConfigurationFormSchema';
import { useFetchQuickBooksSettings } from '../hooks/useFetchQuickBooksSettings';
import { useQuickbooksEnabled } from '../../../../../../../hooks/requests/integrations/quickbooks/use-quickbooks-enabled';
import type {
  QuickBooksConfigurationControl,
  QuickBooksConfigurationFormValues,
} from '../QuickBooksConfigurationFormSchema';
import { CreditMemosConfigurationSection } from './components/CreditMemosConfigurationSection';
import { InvoicesConfigurationSection } from './components/InvoicesConfigurationSection';
import { PaymentsConfigurationSection } from './components/PaymentsConfigurationSection';
import { TaxConfigurationSection } from './components/TaxConfigurationSection';

type QuickBooksConfigurationFormProps = {
  control: QuickBooksConfigurationControl;
};

const defaultConfiguration: QuickBooksConfigurationFormValues = {
  syncCustomer: SYNC_DIRECTIONS.AUTO_SYNC_DISABLED,
  syncApprovedInvoices: false,
};

export const QuickBooksConfigurationForm: FC<QuickBooksConfigurationFormProps> = ({ control }) => {
  const { configuration } = useFetchQuickBooksSettings();
  const isQuickBooksEnabled = useQuickbooksEnabled();
  const { reset } = useFormContext<QuickBooksConfigurationFormValues>();

  const defaultAndConfiguration = {
    ...defaultConfiguration,
    syncApprovedInvoices: configuration.syncApprovedInvoices,
    invoiceNumberSyncStrategy: configuration.invoiceNumberSyncStrategy,
    invoicePaymentStatusSyncStrategy: configuration.invoicePaymentStatusSyncStrategy,
    exportBankDetails: configuration.exportBankDetails,
    defaultStripeDepositToAccountId: configuration.defaultStripeDepositToAccountId,
    defaultDepositToAccountId: configuration.defaultDepositToAccountId,
    taxExportStrategy: configuration.taxExportStrategy,
    accountTaxItem: configuration.accountTaxItem,
  };

  useEffectOnce(() => {
    reset(defaultAndConfiguration);
  });

  return (
    <FormContainer>
      <Typography variant="h5">Configurations</Typography>
      <ConfigurationsContainer>
        {/*
          TODO: Backend not implemented.
          <CustomersConfigurationSection control={control} defaultValues={defaultAndConfiguration} />
        */}
        <InvoicesConfigurationSection control={control} defaultValues={defaultAndConfiguration} />
        <CreditMemosConfigurationSection control={control} defaultValues={defaultAndConfiguration} />
        <TaxConfigurationSection control={control} defaultValues={defaultAndConfiguration} />
        {isQuickBooksEnabled && (
          <PaymentsConfigurationSection control={control} defaultValues={defaultAndConfiguration} />
        )}
      </ConfigurationsContainer>
    </FormContainer>
  );
};

const FormContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});
